import {z} from 'zod'

import {
  BlockHighlightVisualizationEnum,
  ButtonIconEnum,
  ButtonSizeEnum,
  ButtonVariantEnum,
  KpiNumberVariantEnum,
} from '@sdox/api'

import {i18nValidation} from '../../../../i18n'

export type KpiFormSchema = z.infer<typeof kpiFormSchema>
export type ButtonFormSchema = z.infer<typeof buttonSchema>
export type OptionalButtonFormSchema = z.infer<typeof optionalButtonSchema>

export type UnsetBtnType = Extract<OptionalButtonFormSchema, {btnType: 'unset'}>

export const kpiFormSchema = z.union([
  z.object({
    title: z.string().min(1),
    value: z.coerce.number().safe(),
    prefix: z.string().nullish(),
    suffix: z.string().min(1),
    subtitle: z.string().nullish(),
    variant: z.nativeEnum(KpiNumberVariantEnum).or(z.literal('none')),
  }),
  z.object({
    title: z.string().min(1),
    value: z.string().min(1),
    prefix: z.string().nullish(),
    suffix: z.string().min(1),
    subtitle: z.string().nullish(),
    variant: z.undefined(),
  }),
])

const buttonInterfaceSchema = z.object({
  disclaimer: z.string().nullish(),
  label: z.string().nullish(),
  leadingIcon: z.nativeEnum(ButtonIconEnum).nullish(),
  size: z.nativeEnum(ButtonSizeEnum),
  trailingIcon: z.nativeEnum(ButtonIconEnum).nullish(),
  variant: z.nativeEnum(ButtonVariantEnum),
})

const hrefButtonSchema = buttonInterfaceSchema.extend({
  url: z.string().url({message: i18nValidation.invalidUrl}),
})

const copyButtonSchema = buttonInterfaceSchema.extend({
  content: z.string(),
})

const docButtonSchema = buttonInterfaceSchema.extend({
  identifier: z.string(),
})

const nestedContentButtonSchema = buttonInterfaceSchema.extend({
  identifier: z.string().optional(),
})

const downloadButtonSchema = buttonInterfaceSchema.extend({
  media: z.object({
    fileHash: z.string(),
  }),
  variant: z.nativeEnum(ButtonVariantEnum).default(ButtonVariantEnum.Filled),
  fileName: z.string().min(1, {message: i18nValidation.enterText}).default('Anhang'),
})

export const buttonSchema = z.discriminatedUnion('btnType', [
  z.object({
    btnType: z.literal('btnCopy'),
    btnCopy: copyButtonSchema,
  }),
  z.object({
    btnType: z.literal('btnHref'),
    btnHref: hrefButtonSchema,
  }),
  z.object({
    btnType: z.literal('btnDoc'),
    btnDoc: docButtonSchema,
  }),
  z.object({
    btnType: z.literal('btnNestedContent'),
    btnNestedContent: nestedContentButtonSchema,
  }),
  z.object({
    btnType: z.literal('btnDownload'),
    btnDownload: downloadButtonSchema,
  }),
])

export const optionalButtonSchema = z.discriminatedUnion('btnType', [
  z.object({
    btnType: z.literal('unset'),
  }),
  z.object({
    btnType: z.literal('btnCopy'),
    btnCopy: copyButtonSchema,
  }),
  z.object({
    btnType: z.literal('btnHref'),
    btnHref: hrefButtonSchema,
  }),
  z.object({
    btnType: z.literal('btnDoc'),
    btnDoc: docButtonSchema,
  }),
  z.object({
    btnType: z.literal('btnNestedContent'),
    btnNestedContent: nestedContentButtonSchema,
  }),
  z.object({
    btnType: z.literal('btnDownload'),
    btnDownload: downloadButtonSchema,
  }),
])

export const fileOrHashSchema = z.union([
  z.object({
    file: z.instanceof(File),
  }),
  z.object({
    md5: z.string(),
  }),
])

const baseHighlightSchema = z.object({
  cta: buttonSchema,
  image: z.object({
    fileHash: z.string().min(1),
  }),
  imageMobile: z.object({
    fileHash: z.string().min(1),
  }).nullish(),
})

const defaultHighlightSchema = baseHighlightSchema.extend({
  title: z.string().min(1, {message: i18nValidation.enterText}),
  description: z.string().min(1, {message: i18nValidation.enterText}),
})

const textImageSchema = baseHighlightSchema.extend({
  cta: buttonSchema,
  title: z.string().min(1, {message: i18nValidation.enterText}),
  description: z.string().min(1, {message: i18nValidation.enterText}),
})

export const highlightSchema = z.discriminatedUnion('visualization', [
  defaultHighlightSchema.extend({visualization: z.literal(BlockHighlightVisualizationEnum.Default)}),
  defaultHighlightSchema.extend({visualization: z.literal(BlockHighlightVisualizationEnum.ViceVersa)}),
  textImageSchema.extend({visualization: z.literal(BlockHighlightVisualizationEnum.TextImage)}),
  textImageSchema.extend({visualization: z.literal(BlockHighlightVisualizationEnum.TextImageViceVersa)}),
])
